import type { Locale } from "./types";
import { ACTIVE_BRAND, isConsultFree } from "./brands";

// One flat dictionary per locale. Keys are shared; `t()` looks them up.
const en = {
  brandName: "Braces by Garcia",
  freeConsult: "Free Consultation",
  poweredBy: "Secure scheduling",

  step_location: "Location",
  step_time: "Time",
  step_details: "Your info",
  step_review: "Confirm",

  lead_title: "Let's find your appointment",
  lead_sub: "Tell us how to reach you and we'll show the soonest openings.",
  lead_next_opening: "Next opening",
  lead_cta: "See available times",
  lead_sms_consent: "By submitting, you agree to get texts from us. Rates apply.",
  finish_title: "Almost done",
  finish_sub: "A few details to finish your free consultation.",

  loc_title: "Which office works best?",
  loc_subtitle: "Choose the location nearest you.",
  loc_select: "Select",

  time_title: "Pick a time that works for you",
  time_soonest: "Soonest available",
  time_pick_day: "Or pick a day",
  time_open: "open",
  time_more: "See more times",
  time_fewer: "Show fewer",
  time_none: "No online openings in the next few weeks.",
  time_loading: "Finding the soonest openings…",
  time_loading_slow: "Still checking live openings at {office}…",
  time_days_note: "This office books new patients on {days}.",
  slots_error: "We couldn't load available times just now. Please try again.",
  time_morning: "Morning",
  time_afternoon: "Afternoon",
  time_change_office: "Change office",
  time_waitlist_cta: "Request a callback instead",
  time_waitlist_link: "None of these work? Request a callback",

  details_title: "Who is the appointment for?",
  details_first: "Patient first name",
  details_last: "Patient last name",
  details_dob: "Date of birth",
  details_dob_ph: "MM/DD/YYYY",
  details_phone: "Mobile phone",
  details_phone_help: "We only use this to confirm and remind you about your visit.",
  details_email: "Email",
  opt_label: "Optional",
  details_minor_q: "Is the patient under 18?",
  details_yes: "Yes",
  details_no: "No",
  details_guardian_title: "Parent or guardian",
  details_guardian_first: "Your first name",
  details_guardian_last: "Your last name",
  details_relationship: "Relationship to patient",
  rel_parent: "Parent",
  rel_guardian: "Guardian",
  rel_grandparent: "Grandparent",
  rel_self: "Self",
  rel_other: "Other",
  details_reason: "What brings you in?",
  reason_braces: "Braces consultation",
  reason_invisalign: "Invisalign / clear aligners",
  reason_child: "Child's first ortho visit",
  reason_other: "Something else",

  ins_title: "Insurance (optional)",
  ins_note: "Add it now to speed up your first visit, or skip and bring it with you.",
  ins_company: "Insurance company",
  ins_member: "Member ID",
  ins_skip: "Skip for now",
  ins_add: "Add insurance",

  review_title: "Review and confirm",
  review_when: "When",
  review_where: "Where",
  review_patient: "Patient",
  review_contact: "Contact",
  review_recaptcha: "Protected by reCAPTCHA.",
  review_submit: "Confirm my free consult",
  review_booking: "Booking your visit…",
  review_edit: "Edit",
  book_failed: "We couldn't finish that booking. Please pick a time and try again.",
  slot_taken: "That time was just booked by someone else. Here are the openings still available.",
  book_failed_cta: "Pick a new time",

  success_title: "You're all set!",
  success_msg: "We've reserved your free consultation. A confirmation is on its way.",
  success_cal: "Add to calendar",
  success_call: "Questions? Call us",
  success_book_another: "Book another",

  waitlist_title: "Request a callback",
  waitlist_msg: "Leave your info and our team will reach out with the next available times.",
  waitlist_submit: "Request callback",
  waitlist_done: "Thanks! We'll be in touch soon.",

  handoff_title: "Choose your appointment time",
  handoff_msg: "Pick a time below to finish booking your free consultation.",
  handoff_fallback: "Trouble loading the scheduler?",
  handoff_cta: "Open it in a new tab",

  c_next: "Continue",
  c_back: "Back",
  c_required: "Required",
  c_invalid_dob: "Enter a valid date as MM/DD/YYYY.",
  c_invalid_phone: "Enter a valid phone number.",
  c_invalid_name: "Please enter a name.",
  c_invalid_email: "Please enter a valid email address.",
  c_error: "Something went wrong. Please try again or call us.",
  c_retry: "Try again",
  c_lang: "Español",
};

type Key = keyof typeof en;

const es: Record<Key, string> = {
  brandName: "Braces by Garcia",
  freeConsult: "Consulta gratuita",
  poweredBy: "Agendamiento seguro",

  step_location: "Ubicación",
  step_time: "Horario",
  step_details: "Tus datos",
  step_review: "Confirmar",

  lead_title: "Encontremos tu cita",
  lead_sub: "Dinos cómo contactarte y te mostramos las citas más próximas.",
  lead_next_opening: "Próxima cita",
  lead_cta: "Ver horarios disponibles",
  lead_sms_consent: "Al enviar, aceptas recibir mensajes de texto de nosotros. Pueden aplicar tarifas.",
  finish_title: "Casi listo",
  finish_sub: "Unos datos más para completar tu consulta gratuita.",

  loc_title: "¿Qué oficina te queda mejor?",
  loc_subtitle: "Elige la ubicación más cercana.",
  loc_select: "Elegir",

  time_title: "Elige el horario que mejor te funcione",
  time_soonest: "Disponible más pronto",
  time_pick_day: "O elige un día",
  time_open: "libres",
  time_more: "Ver más horarios",
  time_fewer: "Ver menos",
  time_none: "No hay citas en línea en las próximas semanas.",
  time_loading: "Buscando las citas más próximas…",
  time_loading_slow: "Seguimos revisando los horarios en {office}…",
  time_days_note: "Esta oficina agenda pacientes nuevos los {days}.",
  slots_error: "No pudimos cargar los horarios en este momento. Inténtalo de nuevo.",
  time_morning: "Mañana",
  time_afternoon: "Tarde",
  time_change_office: "Cambiar oficina",
  time_waitlist_cta: "Prefiero que me llamen",
  time_waitlist_link: "¿Ningún horario te funciona? Solicita una llamada",

  details_title: "¿Para quién es la cita?",
  details_first: "Nombre del paciente",
  details_last: "Apellido del paciente",
  details_dob: "Fecha de nacimiento",
  details_dob_ph: "MM/DD/AAAA",
  details_phone: "Teléfono celular",
  details_phone_help: "Solo lo usamos para confirmar y recordarte tu cita.",
  details_email: "Correo electrónico",
  opt_label: "Opcional",
  details_minor_q: "¿El paciente es menor de 18 años?",
  details_yes: "Sí",
  details_no: "No",
  details_guardian_title: "Padre, madre o tutor",
  details_guardian_first: "Tu nombre",
  details_guardian_last: "Tu apellido",
  details_relationship: "Parentesco con el paciente",
  rel_parent: "Padre/Madre",
  rel_guardian: "Tutor",
  rel_grandparent: "Abuelo/a",
  rel_self: "Yo mismo/a",
  rel_other: "Otro",
  details_reason: "¿Qué te trae con nosotros?",
  reason_braces: "Consulta de frenos",
  reason_invisalign: "Invisalign / alineadores",
  reason_child: "Primera visita de ortodoncia del niño/a",
  reason_other: "Otra cosa",

  ins_title: "Seguro (opcional)",
  ins_note: "Agrégalo ahora para agilizar tu primera visita, o tráelo contigo.",
  ins_company: "Compañía de seguro",
  ins_member: "Número de miembro",
  ins_skip: "Omitir por ahora",
  ins_add: "Agregar seguro",

  review_title: "Revisa y confirma",
  review_when: "Cuándo",
  review_where: "Dónde",
  review_patient: "Paciente",
  review_contact: "Contacto",
  review_recaptcha: "Protegido por reCAPTCHA.",
  review_submit: "Confirmar mi consulta gratis",
  review_booking: "Agendando tu cita…",
  review_edit: "Editar",
  book_failed: "No pudimos completar la reserva. Elige un horario e inténtalo de nuevo.",
  slot_taken: "Otra persona acaba de reservar ese horario. Estos son los horarios que siguen disponibles.",
  book_failed_cta: "Elegir otro horario",

  success_title: "¡Listo!",
  success_msg: "Reservamos tu consulta gratuita. Pronto recibirás una confirmación.",
  success_cal: "Agregar al calendario",
  success_call: "¿Preguntas? Llámanos",
  success_book_another: "Agendar otra",

  waitlist_title: "Solicita una llamada",
  waitlist_msg: "Déjanos tus datos y nuestro equipo te contactará con los próximos horarios.",
  waitlist_submit: "Solicitar llamada",
  waitlist_done: "¡Gracias! Te contactaremos pronto.",

  handoff_title: "Elige la hora de tu cita",
  handoff_msg: "Elige una hora abajo para completar tu consulta gratuita.",
  handoff_fallback: "¿Problemas para cargar el agendador?",
  handoff_cta: "Ábrelo en una pestaña nueva",

  c_next: "Continuar",
  c_back: "Atrás",
  c_required: "Requerido",
  c_invalid_dob: "Ingresa una fecha válida como MM/DD/AAAA.",
  c_invalid_phone: "Ingresa un número de teléfono válido.",
  c_invalid_name: "Por favor ingresa un nombre.",
  c_invalid_email: "Por favor ingresa un correo electrónico válido.",
  c_error: "Algo salió mal. Inténtalo de nuevo o llámanos.",
  c_retry: "Reintentar",
  c_lang: "English",
};

export const DICTS: Record<Locale, Record<Key, string>> = { en, es };

// Consult wording for a brand that charges for the visit (BrandConfig
// .consultIsFree === false). Only the keys that name the price are overridden;
// everything else comes from the locale dictionary as usual. The replacements
// are neutral rather than "paid": Beglin charges for ADULT consultations only,
// and the office quotes the fee, so the widget states no price either way.
export const PAID_CONSULT: Record<Locale, Partial<Record<Key, string>>> = {
  en: {
    freeConsult: "Consultation",
    finish_sub: "A few details to finish your consultation.",
    review_submit: "Confirm my consultation",
    success_msg: "We've reserved your consultation. A confirmation is on its way.",
    handoff_msg: "Pick a time below to finish booking your consultation.",
  },
  es: {
    freeConsult: "Consulta",
    finish_sub: "Unos datos más para completar tu consulta.",
    review_submit: "Confirmar mi consulta",
    success_msg: "Reservamos tu consulta. Pronto recibirás una confirmación.",
    handoff_msg: "Elige una hora abajo para completar tu consulta.",
  },
};

export type { Key as I18nKey };

export function makeT(locale: Locale) {
  const dict = DICTS[locale] ?? en;
  // Brands that charge override the handful of price-naming keys; free-consult
  // brands (all but Beglin) get an empty override and the dictionary as-is.
  const paid = isConsultFree(ACTIVE_BRAND) ? undefined : PAID_CONSULT[locale];
  // brandName is the active brand's proper name (same across locales); every
  // other key comes from the per-locale dictionary.
  return (key: Key): string =>
    key === "brandName" ? ACTIVE_BRAND.brandName : paid?.[key] ?? dict[key] ?? en[key] ?? key;
}
